import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { invoiceApi, billingApi, clientApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, EmptyState } from '../components/common';
import { formatCurrency, formatDate, copyToClipboard } from '../utils/helpers';

const statusColors = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  overdue: 'error',
  cancelled: 'default',
  refunded: 'warning',
};

const Billing = () => {
  const navigate = useNavigate();
  const { showSnackbar, fetchDashboardData } = useApp();

  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, limit: 25, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');

  // Create invoice dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    client: '',
    amount: 40,
    periodStart: new Date(),
    periodEnd: new Date(),
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  });

  // Mark paid dialog
  const [paidDialog, setPaidDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    paymentMethod: 'stripe',
    paymentReference: '',
    paidDate: new Date(),
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, summaryRes, clientsRes] = await Promise.all([
        invoiceApi.getAll({
          page: pagination.page + 1,
          limit: pagination.limit,
          status: statusFilter || undefined,
        }),
        billingApi.getSummary(),
        clientApi.getAll({ status: 'active', limit: 100 }),
      ]);
      setInvoices(invoicesRes.data.invoices);
      setPagination(prev => ({ ...prev, total: invoicesRes.data.pagination.total }));
      setSummary(summaryRes.data);
      setClients(clientsRes.data.clients);
    } catch (error) {
      showSnackbar('Failed to load billing data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, statusFilter]);

  const handleGenerateMonthly = async () => {
    try {
      const result = await invoiceApi.generateMonthly();
      showSnackbar(`Generated ${result.data.generated} invoices`, 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to generate invoices', 'error');
    }
  };

  const handleCreateInvoice = async () => {
    try {
      await invoiceApi.create({
        client: newInvoice.client,
        amount: newInvoice.amount,
        taxAmount: 0,
        totalAmount: newInvoice.amount,
        periodStart: newInvoice.periodStart,
        periodEnd: newInvoice.periodEnd,
        dueDate: newInvoice.dueDate,
        issueDate: new Date(),
        status: 'draft',
      });
      showSnackbar('Invoice created', 'success');
      setCreateDialog(false);
      setNewInvoice({
        client: '',
        amount: 40,
        periodStart: new Date(),
        periodEnd: new Date(),
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      });
      fetchData();
    } catch (error) {
      showSnackbar('Failed to create invoice', 'error');
    }
  };

  const handleSendInvoice = async (invoice) => {
    try {
      await invoiceApi.send(invoice._id);
      showSnackbar('Invoice sent', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to send invoice', 'error');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await invoiceApi.markPaid(selectedInvoice._id, paymentData);
      showSnackbar('Invoice marked as paid', 'success');
      setPaidDialog(false);
      setSelectedInvoice(null);
      fetchData();
      fetchDashboardData();
    } catch (error) {
      showSnackbar('Failed to mark invoice as paid', 'error');
    }
  };

  const handleCopyPaymentLink = (invoice) => {
    if (invoice.stripePaymentLink) {
      copyToClipboard(invoice.stripePaymentLink);
      showSnackbar('Payment link copied', 'success');
    }
  };

  if (loading && invoices.length === 0) {
    return <LoadingSpinner message="Loading billing..." />;
  }

  return (
    <Box>
      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="overline" color="text.secondary">
                  Expected This Month
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {formatCurrency(summary.expectedRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="overline" color="success.dark">
                  Collected
                </Typography>
                <Typography variant="h4" fontWeight={700} color="success.dark">
                  {formatCurrency(summary.collectedRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.light' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="overline" color="warning.dark">
                  Pending
                </Typography>
                <Typography variant="h4" fontWeight={700} color="warning.dark">
                  {formatCurrency(summary.pendingRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: summary.overdueInvoices > 0 ? 'error.light' : 'grey.100' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="overline" color={summary.overdueInvoices > 0 ? 'error.dark' : 'text.secondary'}>
                  Overdue Invoices
                </Typography>
                <Typography variant="h4" fontWeight={700} color={summary.overdueInvoices > 0 ? 'error.dark' : 'text.primary'}>
                  {summary.overdueInvoices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 150 }}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleGenerateMonthly}
          >
            Generate Monthly
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Create Invoice
          </Button>
        </Box>
      </Paper>

      {/* Invoices Table */}
      <Paper>
        {invoices.length === 0 ? (
          <EmptyState
            title="No invoices found"
            description="Create your first invoice or generate monthly invoices"
            actionLabel="Create Invoice"
            onAction={() => setCreateDialog(true)}
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {invoice.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', color: 'primary.main' }}
                          onClick={() => navigate(`/clients/${invoice.client?._id}`)}
                        >
                          {invoice.client?.businessName || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDate(invoice.periodStart, 'MMM yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(invoice.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          size="small"
                          color={statusColors[invoice.status]}
                          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            invoice.status !== 'paid' && new Date(invoice.dueDate) < new Date()
                              ? 'error.main'
                              : 'text.primary'
                          }
                        >
                          {formatDate(invoice.dueDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {invoice.status === 'draft' && (
                            <Tooltip title="Send Invoice">
                              <IconButton
                                size="small"
                                onClick={() => handleSendInvoice(invoice)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                            <Tooltip title="Mark as Paid">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setPaidDialog(true);
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {invoice.stripePaymentLink && (
                            <Tooltip title="Copy Payment Link">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyPaymentLink(invoice)}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page}
              onPageChange={(e, p) => setPagination(prev => ({ ...prev, page: p }))}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 0 }))}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Create Invoice Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Invoice</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Client"
              value={newInvoice.client}
              onChange={(e) => setNewInvoice({ ...newInvoice, client: e.target.value })}
              fullWidth
            >
              {clients.map((client) => (
                <MenuItem key={client._id} value={client._id}>
                  {client.businessName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount"
              type="number"
              value={newInvoice.amount}
              onChange={(e) => setNewInvoice({ ...newInvoice, amount: parseFloat(e.target.value) })}
              fullWidth
              InputProps={{ startAdornment: '$' }}
            />
            <DatePicker
              label="Period Start"
              value={newInvoice.periodStart}
              onChange={(date) => setNewInvoice({ ...newInvoice, periodStart: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="Period End"
              value={newInvoice.periodEnd}
              onChange={(date) => setNewInvoice({ ...newInvoice, periodEnd: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="Due Date"
              value={newInvoice.dueDate}
              onChange={(date) => setNewInvoice({ ...newInvoice, dueDate: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInvoice}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={paidDialog} onClose={() => setPaidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Invoice as Paid</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              Marking invoice {selectedInvoice?.invoiceNumber} for {formatCurrency(selectedInvoice?.totalAmount)} as paid
            </Alert>
            <TextField
              select
              label="Payment Method"
              value={paymentData.paymentMethod}
              onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
              fullWidth
            >
              <MenuItem value="stripe">Stripe</MenuItem>
              <MenuItem value="paypal">PayPal</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              label="Payment Reference"
              value={paymentData.paymentReference}
              onChange={(e) => setPaymentData({ ...paymentData, paymentReference: e.target.value })}
              fullWidth
              placeholder="Transaction ID, check number, etc."
            />
            <DatePicker
              label="Payment Date"
              value={paymentData.paidDate}
              onChange={(date) => setPaymentData({ ...paymentData, paidDate: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleMarkPaid}>
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
